import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { TrendingUp, Trophy, Flag, Target, CheckCircle, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getRaceFlag, getRaceFlagIcon } from '../utils/countryFlags';
import { formatDriverName, formatDriverNameLastFirst } from '../utils/formatName';
import {
  getDrivers,
  getTeams,
  getRaces,
  createRacePrediction,
  createQualifyingPrediction,
  createChampionshipPrediction,
  getPredictionStatus,
  getChampionshipStatus,
  checkDeadline,
  checkChampionshipDeadline,
  getMyPredictions,
  getMyChampionshipPrediction,
  getSeasonSettings,
  getTestSeason,
  getTestRaces,
  deleteMyPrediction,
} from '../services/api';

const Voorspel = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const hasFetched = useRef(false);

  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [raceStatus, setRaceStatus] = useState(null);
  const [championshipStatus, setChampionshipStatus] = useState(null);
  const [raceDeadlineInfo, setRaceDeadlineInfo] = useState(null);
  const [championshipDeadlineInfo, setChampionshipDeadlineInfo] = useState(null);
  
  // Season settings (toggles from admin)
  const [seasonSettings, setSeasonSettings] = useState({
    race_top5_enabled: true,
    qualifying_top3_enabled: true,
    qualifying_p2p3_enabled: true,
    pole_time_enabled: true,
    fastest_lap_enabled: true,
    safety_car_enabled: true,
    virtual_safety_car_enabled: true,
    dnf_count_enabled: true,
    rain_race_enabled: true,
    world_champion_enabled: true,
    constructor_champion_enabled: true,
    sprint_race_enabled: true,
    sprint_qualifying_enabled: true,
    sprint_pole_winner_enabled: true
  });

  // Race predictions
  const [selectedRace, setSelectedRace] = useState('');
  const [top5P1, setTop5P1] = useState('');
  const [top5P2, setTop5P2] = useState('');
  const [top5P3, setTop5P3] = useState('');
  const [top5P4, setTop5P4] = useState('');
  const [top5P5, setTop5P5] = useState('');
  const [fastestLap, setFastestLap] = useState('');
  const [safetyCar, setSafetyCar] = useState('');
  const [virtualSafetyCar, setVirtualSafetyCar] = useState('');
  const [dnfCount, setDnfCount] = useState('');
  const [rainRace, setRainRace] = useState('');
  
  // Sprint predictions
  const [sprintWinner, setSprintWinner] = useState('');
  const [sprintPoleWinner, setSprintPoleWinner] = useState('');
  const [sprintPoleTime, setSprintPoleTime] = useState('');
  
  // Qualifying predictions
  const [qualifyingWinner, setQualifyingWinner] = useState('');
  const [qualifyingP2, setQualifyingP2] = useState('');
  const [qualifyingP3, setQualifyingP3] = useState('');
  const [poleTime, setPoleTime] = useState('');

  // Championship predictions - Top 3
  const [worldChampionTop3, setWorldChampionTop3] = useState(['', '', '']);
  const [constructorChampionTop3, setConstructorChampionTop3] = useState(['', '', '']);
  
  // Backwards compatibility - single values for older code
  const worldChampion = worldChampionTop3[0];
  const constructorChampion = constructorChampionTop3[0];
  const setWorldChampion = (val) => setWorldChampionTop3(prev => [val, prev[1], prev[2]]);
  const setConstructorChampion = (val) => setConstructorChampionTop3(prev => [val, prev[1], prev[2]]);
  
  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Success banner state
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Helper function to load predictions for a race
  const loadPredictionsForRace = async (raceId) => {
    try {
      const predictions = await getMyPredictions();
      
      // Find race prediction
      const racePred = predictions.find(p => p.race_id === raceId && p.type === 'race');
      if (racePred) {
        // Load top 5 drivers
        setTop5P1(racePred.top5?.[0] || '');
        setTop5P2(racePred.top5?.[1] || '');
        setTop5P3(racePred.top5?.[2] || '');
        setTop5P4(racePred.top5?.[3] || '');
        setTop5P5(racePred.top5?.[4] || '');
        // Load other race predictions
        setFastestLap(racePred.fastest_lap || '');
        setSafetyCar(racePred.safety_car !== undefined && racePred.safety_car !== null ? String(racePred.safety_car) : '');
        setVirtualSafetyCar(racePred.virtual_safety_car !== undefined && racePred.virtual_safety_car !== null ? String(racePred.virtual_safety_car) : '');
        setDnfCount(racePred.dnf_count !== undefined && racePred.dnf_count !== null ? String(racePred.dnf_count) : '');
        setRainRace(racePred.rain_race !== undefined && racePred.rain_race !== null ? String(racePred.rain_race) : '');
        // Load sprint predictions
        setSprintWinner(racePred.sprint_winner || '');
        setSprintPoleWinner(racePred.sprint_pole_winner || '');
        setSprintPoleTime(racePred.sprint_pole_time || '');
      } else {
        // Reset race fields if no prediction found
        setTop5P1('');
        setTop5P2('');
        setTop5P3('');
        setTop5P4('');
        setTop5P5('');
        setFastestLap('');
        setSafetyCar('');
        setVirtualSafetyCar('');
        setDnfCount('');
        setRainRace('');
        setSprintWinner('');
        setSprintPoleWinner('');
        setSprintPoleTime('');
      }
      
      // Find qualifying prediction
      const qualPred = predictions.find(p => p.race_id === raceId && p.type === 'qualifying');
      if (qualPred) {
        // Load qualifying positions
        setQualifyingWinner(qualPred.qualifying?.[0] || '');
        setQualifyingP2(qualPred.qualifying?.[1] || '');
        setQualifyingP3(qualPred.qualifying?.[2] || '');
        // Load pole time
        setPoleTime(qualPred.pole_time || '');
      } else {
        // Reset qualifying fields if no prediction found
        setQualifyingWinner('');
        setQualifyingP2('');
        setQualifyingP3('');
        setPoleTime('');
      }
    } catch (error) {
      console.error('Error loading predictions:', error);
    }
  };

  // Helper function to load championship predictions
  const loadChampionshipPredictions = async () => {
    try {
      const champPred = await getMyChampionshipPrediction(2026);
      if (champPred) {
        // Load top 3 arrays (or single values for backwards compatibility)
        const wdcTop3 = champPred.world_champion_top3 || [champPred.world_champion || '', '', ''];
        const wccTop3 = champPred.constructor_champion_top3 || [champPred.constructor_champion || '', '', ''];
        setWorldChampionTop3(wdcTop3);
        setConstructorChampionTop3(wccTop3);
      }
    } catch (error) {
      console.error('Error loading championship predictions:', error);
    }
  };

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Prevent multiple fetches (React StrictMode can cause double-mount)
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;
    
    const fetchData = async () => {
      try {
        const [driversData, teamsData, racesData, settings, testSeasonData, testRacesData] = await Promise.all([
          getDrivers(2026),
          getTeams(2026),
          getRaces(2026),
          getSeasonSettings(2026).catch(() => seasonSettings),
          getTestSeason().catch(() => null),
          getTestRaces().catch(() => []),
        ]);
        
        // Validate that we got data
        if (!driversData || !teamsData || !racesData) {
          throw new Error('Geen data ontvangen van server');
        }
        
        // Sort drivers alphabetically by last name
        const sortedDrivers = [...driversData].sort((a, b) => {
          const lastNameA = a.name.split(' ').pop().toUpperCase();
          const lastNameB = b.name.split(' ').pop().toUpperCase();
          return lastNameA.localeCompare(lastNameB, 'nl');
        });
        
        // Combine regular races with test races if test season is active
        let allRaces = [...racesData];
        if (testSeasonData && testRacesData && testRacesData.length > 0) {
          // Mark test races with a special flag for UI purposes
          const markedTestRaces = testRacesData.map(race => ({
            ...race,
            isTestRace: true,
            name: `🧪 ${race.name}` // Add visual indicator
          }));
          // Put test races at the beginning since they happen before the regular season
          allRaces = [...markedTestRaces, ...racesData];
        }
        
        setDrivers(sortedDrivers);
        // Sort teams alphabetically
        const sortedTeams = [...teamsData].sort((a, b) => a.name.localeCompare(b.name, 'nl'));
        setTeams(sortedTeams);
        setRaces(allRaces);
        setSeasonSettings(settings);
        
        // Find the first upcoming race with an open deadline
        // Sort by round first to ensure correct order
        const upcomingRaces = allRaces
          .filter(r => r.status === 'upcoming')
          .sort((a, b) => (a.round || 0) - (b.round || 0));
        let selectedUpcomingRace = null;
        
        // Check deadlines for upcoming races to find first one still open
        for (const race of upcomingRaces) {
          try {
            const deadlineInfo = await checkDeadline(race.race_id);
            if (!deadlineInfo.deadline_passed) {
              selectedUpcomingRace = race;
              setRaceDeadlineInfo(deadlineInfo);
              break;
            }
          } catch (err) {
            // If deadline check fails, assume race is still open
            selectedUpcomingRace = race;
            break;
          }
        }
        
        // Fallback to first upcoming race if none found with open deadline
        if (!selectedUpcomingRace && upcomingRaces.length > 0) {
          selectedUpcomingRace = upcomingRaces[0];
        }
        
        if (selectedUpcomingRace) {
          setSelectedRace(selectedUpcomingRace.race_id);
          
          // Check if user has predictions for this race (silent fail ok)
          try {
            const status = await getPredictionStatus(selectedUpcomingRace.race_id);
            setRaceStatus(status);
          } catch (err) {
            // Ignore - user may not have predictions yet
          }
          
          // Load existing predictions for this race (silent fail ok)
          try {
            await loadPredictionsForRace(selectedUpcomingRace.race_id);
          } catch (err) {
            // Ignore - no existing predictions
          }
        } else if (allRaces.length > 0) {
          setSelectedRace(allRaces[0].race_id);
        }
        
        // Check championship status (silent fail ok)
        try {
          const champStatus = await getChampionshipStatus(2026);
          setChampionshipStatus(champStatus);
        } catch (err) {
          // Ignore
        }
        
        // Check championship deadline (silent fail ok)
        try {
          const champDeadlineInfo = await checkChampionshipDeadline(2026);
          setChampionshipDeadlineInfo(champDeadlineInfo);
        } catch (err) {
          // Ignore
        }
        
        // Load championship predictions (silent fail ok)
        try {
          await loadChampionshipPredictions();
        } catch (err) {
          // Ignore
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Only show error if it's not an auth error (those redirect automatically)
        if (error.response?.status !== 401 && error.response?.status !== 429) {
          toast({
            title: 'Fout',
            description: 'Kon data niet laden. Probeer de pagina te verversen.',
            variant: 'destructive',
          });
        } else if (error.response?.status === 429) {
          // Rate limited - wait and retry silently
          console.log('Rate limited, data will load on next visit');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle delete prediction
  const handleDeletePrediction = async () => {
    if (!selectedRace) return;
    
    setDeleting(true);
    try {
      await deleteMyPrediction(selectedRace);
      
      // Reset form
      setTop5P1('');
      setTop5P2('');
      setTop5P3('');
      setTop5P4('');
      setTop5P5('');
      setFastestLap('');
      setSafetyCar('');
      setVirtualSafetyCar('');
      setDnfCount('');
      setRainRace('');
      setSprintWinner('');
      setSprintPoleWinner('');
      setSprintPoleTime('');
      setQualifyingWinner('');
      setQualifyingP2('');
      setQualifyingP3('');
      setPoleTime('');
      
      // Update status
      setRaceStatus({ has_race_prediction: false, has_qualifying_prediction: false, is_complete: false });
      
      toast({
        title: 'Voorspelling verwijderd',
        description: 'Je voorspelling is succesvol verwijderd',
      });
      
      setShowDeleteConfirm(false);
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon voorspelling niet verwijderen',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleRacePrediction = async (e) => {
    e.preventDefault();
    
    const currentRace = races.find(r => r.race_id === selectedRace);
    const isSprintWeekend = currentRace?.is_sprint_weekend;
    const isTestRace = currentRace?.isTestRace || currentRace?.name?.includes('🧪') || currentRace?.name?.toLowerCase().includes('test');
    
    // Check if this is the first official race of the season (round 1, not test)
    if (!isTestRace && currentRace?.round === 1) {
      // Check if user has filled in championship predictions
      try {
        const champStatus = await getChampionshipStatus(2026);
        const hasChampionshipPrediction = champStatus?.has_prediction;
        
        if (!hasChampionshipPrediction) {
          // Check if championship deadline has passed
          const champDeadline = await checkChampionshipDeadline(2026);
          if (!champDeadline?.deadline_passed) {
            toast({
              title: '⚠️ Kampioenschap voorspelling vereist',
              description: 'Voordat je je eerste race voorspelling kunt opslaan, moet je eerst je Kampioenschap voorspellingen (Wereldkampioen & Constructeurskampioen) invullen op het Kampioenschap tabblad.',
              variant: 'destructive',
              duration: 8000,
            });
            return;
          }
        }
      } catch (err) {
        console.error('Error checking championship status:', err);
        // Continue anyway if check fails
      }
    }
    
    // Check if at least something is filled in (allow partial saving)
    const hasAnyRaceData = top5P1 || top5P2 || top5P3 || top5P4 || top5P5 || fastestLap || safetyCar || virtualSafetyCar || dnfCount || rainRace;
    const hasAnyQualifyingData = qualifyingWinner || poleTime || qualifyingP2 || qualifyingP3;
    const hasAnySprintData = isSprintWeekend && (sprintWinner || sprintPoleWinner || sprintPoleTime);
    
    if (!hasAnyRaceData && !hasAnyQualifyingData && !hasAnySprintData) {
      toast({
        title: 'Fout',
        description: 'Vul minimaal één voorspelling in om op te slaan',
        variant: 'destructive',
      });
      return;
    }

    // Validate duplicates in top 5 (only check filled values)
    const top5 = [top5P1, top5P2, top5P3, top5P4, top5P5].filter(Boolean);
    if (top5.length > 0 && new Set(top5).size !== top5.length) {
      toast({
        title: 'Fout',
        description: 'Je kunt elke coureur maar één keer selecteren in de top 5',
        variant: 'destructive',
      });
      return;
    }

    // Validate qualifying duplicates (only check filled values)
    const qualifying = seasonSettings.qualifying_p2p3_enabled 
      ? [qualifyingWinner, qualifyingP2, qualifyingP3].filter(Boolean)
      : [qualifyingWinner].filter(Boolean);
    if (qualifying.length > 1 && new Set(qualifying).size !== qualifying.length) {
      toast({
        title: 'Fout',
        description: 'Je kunt elke coureur maar één keer selecteren in de kwalificatie',
        variant: 'destructive',
      });
      return;
    }

    // Validate pole time format if filled (MM:SS.mmm)
    if (poleTime) {
      const poleTimeRegex = /^[0-5]?[0-9]:[0-5][0-9]\.[0-9]{3}$/;
      if (!poleTimeRegex.test(poleTime)) {
        toast({
          title: 'Ongeldige Pole Tijd',
          description: 'Pole tijd moet in het formaat MM:SS.mmm zijn (bijv. 01:30.456 of 1:30.456)',
          variant: 'destructive',
        });
        return;
      }
    }

    // Validate sprint pole time format if filled
    if (isSprintWeekend && sprintPoleTime) {
      const poleTimeRegex = /^[0-5]?[0-9]:[0-5][0-9]\.[0-9]{3}$/;
      if (!poleTimeRegex.test(sprintPoleTime)) {
        toast({
          title: 'Ongeldige Sprint Pole Tijd',
          description: 'Sprint pole tijd moet in het formaat MM:SS.mmm zijn (bijv. 01:28.456)',
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      // Prepare sprint data if applicable (only include filled values)
      const sprintData = isSprintWeekend ? {
        sprint_winner: sprintWinner || null,
        sprint_pole_winner: sprintPoleWinner || null,
        sprint_pole_time: sprintPoleTime || null,
      } : null;
      
      // Build qualifying array (only include filled values)
      const qualifyingArray = seasonSettings.qualifying_p2p3_enabled 
        ? [qualifyingWinner, qualifyingP2, qualifyingP3].filter(Boolean)
        : [qualifyingWinner].filter(Boolean);
      
      // Build top5 array (only include filled values)
      const top5Array = [top5P1, top5P2, top5P3, top5P4, top5P5];
      
      // Save race prediction if any race data is filled
      if (hasAnyRaceData || hasAnySprintData) {
        // Don't send confirmation email if qualifying will follow
        const willSaveQualifying = hasAnyQualifyingData && qualifyingArray.length > 0;
        await createRacePrediction(
          selectedRace, 
          top5P1 || null, 
          top5Array, 
          fastestLap || null, 
          safetyCar || null, 
          virtualSafetyCar || null, 
          dnfCount || null, 
          rainRace || null, 
          sprintData,
          !willSaveQualifying  // only send confirmation if qualifying won't follow
        );
      }
      
      // Save qualifying prediction if any qualifying data is filled
      if (hasAnyQualifyingData && qualifyingArray.length > 0) {
        await createQualifyingPrediction(selectedRace, qualifyingArray, poleTime || null);
      }
      
      // Scroll to top first
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show success banner
      const raceName = races.find(r => r.race_id === selectedRace)?.name || 'de race';
      setSuccessMessage(`Je voorspellingen voor ${raceName} zijn succesvol opgeslagen! Je kunt later verder invullen.`);
      setShowSuccessBanner(true);
      
      // Hide banner after 8 seconds
      setTimeout(() => setShowSuccessBanner(false), 8000);
      
      // Show success toast
      toast({
        title: '✅ Voorspellingen opgeslagen!',
        description: 'Je race en kwalificatie voorspellingen zijn succesvol ingediend. Je ontvangt een bevestigingsmail.',
        duration: 6000,
      });
      
      // Update status
      const status = await getPredictionStatus(selectedRace);
      setRaceStatus(status);
      
      // Don't reset fields - keep showing what user submitted
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon voorspelling niet opslaan',
        variant: 'destructive',
      });
    }
  };

  const handleChampionshipPrediction = async (e) => {
    e.preventDefault();
    
    // Double-check deadline on frontend
    if (championshipDeadlineInfo?.deadline_passed) {
      toast({
        title: 'Deadline Verstreken',
        description: 'Je kunt je kampioenschap voorspelling niet meer wijzigen.',
        variant: 'destructive',
      });
      return;
    }
    
    // Validate at least P1 is filled for both
    if (!worldChampionTop3[0] || !constructorChampionTop3[0]) {
      toast({
        title: 'Fout',
        description: 'Vul minimaal de nummer 1 voorspelling in voor beide kampioenschappen',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createChampionshipPrediction(
        worldChampionTop3[0], 
        constructorChampionTop3[0],
        worldChampionTop3,
        constructorChampionTop3
      );
      
      // Scroll to top first
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Show success banner
      setSuccessMessage('Je kampioenschap voorspellingen (top 3) zijn succesvol opgeslagen!');
      setShowSuccessBanner(true);
      
      // Hide banner after 8 seconds
      setTimeout(() => setShowSuccessBanner(false), 8000);
      
      // Show success toast
      toast({
        title: '✅ Voorspelling opgeslagen!',
        description: 'Je kampioenschap voorspellingen zijn succesvol ingediend',
        duration: 6000,
      });
      
      // Update championship status
      const champStatus = await getChampionshipStatus(2026);
      setChampionshipStatus(champStatus);
      
      // Don't reset - keep showing what user submitted
    } catch (error) {
      toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon voorspelling niet opslaan',
        variant: 'destructive',
      });
    }
  };

  const selectedRaceData = races.find(r => r.race_id === selectedRace);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Laden...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Inactive User Warning Banner */}
        {user && !user.is_active && (
          <div className="mb-6 bg-amber-100 dark:bg-amber-900/30 border-2 border-amber-500 rounded-lg p-4 flex items-start">
            <div className="bg-amber-500 rounded-full p-2 mr-3 mt-0.5">
              <Target className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-bold text-amber-800 dark:text-amber-200 text-lg">Account Nog Niet Geactiveerd</h3>
              <p className="text-amber-700 dark:text-amber-300">
                Je account is nog niet geactiveerd door een admin. Totdat je account is geactiveerd kun je geen voorspellingen indienen.
                Neem contact op met de beheerder via <a href="mailto:info@sportpooltjes.nl" className="underline font-semibold">info@sportpooltjes.nl</a> om je account te laten activeren.
              </p>
            </div>
          </div>
        )}

        {/* Success Banner */}
        {showSuccessBanner && (
          <div className="mb-6 bg-green-500/20 dark:bg-green-900/40 border-2 border-green-500 rounded-lg p-4 flex items-center justify-between animate-pulse">
            <div className="flex items-center">
              <CheckCircle className="text-green-600 dark:text-green-400 mr-3" size={28} />
              <div>
                <h3 className="font-bold text-green-800 dark:text-green-300 text-lg">Voorspelling Opgeslagen!</h3>
                <p className="text-green-700 dark:text-green-400">{successMessage}</p>
              </div>
            </div>
            <button 
              onClick={() => setShowSuccessBanner(false)}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 font-bold text-xl"
            >
              ×
            </button>
          </div>
        )}

        <div className="text-center mb-8">
          <div className="inline-block bg-[#FDB913] rounded-full p-4 mb-4">
            <TrendingUp size={40} className="text-[#1a2332]" />
          </div>
          <h1 className="text-4xl font-bold text-foreground mb-2">Maak Je Voorspelling</h1>
          <p className="text-muted-foreground">Voorspel de race uitslagen en win punten!</p>
        </div>

        <Tabs defaultValue="race" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="race" className="data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332] relative">
              <Flag className="mr-2" size={16} />
              Race & Kwalificatie
              {raceStatus?.is_complete && (
                <CheckCircle className="ml-2 text-green-600" size={16} />
              )}
            </TabsTrigger>
            <TabsTrigger value="championship" className="data-[state=active]:bg-[#FDB913] data-[state=active]:text-[#1a2332] relative">
              <Trophy className="mr-2" size={16} />
              Kampioenschap
              {championshipStatus?.has_prediction && (
                <CheckCircle className="ml-2 text-green-600" size={16} />
              )}
            </TabsTrigger>
          </TabsList>

          {/* Race & Qualifying Predictions Combined */}
          <TabsContent value="race">
            <Card className="border-2 border-[#FDB913]">
              <CardHeader className="bg-gradient-to-r from-blue-100 to-blue-50 dark:from-[#1a2332] dark:to-[#2d3e50]">
                <CardTitle className="text-blue-900 dark:text-[#FDB913]">Race & Kwalificatie Voorspelling</CardTitle>
                <CardDescription className="text-blue-700 dark:text-gray-300">
                  Voorspel de race resultaten en kwalificatie top 3
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Deadline Warning */}
                {raceDeadlineInfo?.deadline_passed && (
                  <div className="bg-red-500/20 dark:bg-red-900/40 border-2 border-red-500 rounded-lg p-4 mb-6">
                    <p className="text-red-800 dark:text-red-300 font-bold text-center">
                      ⚠️ DEADLINE VERSTREKEN
                    </p>
                    <p className="text-red-700 dark:text-red-400 text-center mt-2">
                      De voorspellingen voor deze race zijn gesloten. Je kunt geen voorspellingen meer indienen of wijzigen.
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleRacePrediction} className="space-y-6">
                  {/* Race Selection */}
                  <div>
                    <Label className="text-base font-semibold mb-2 block">Selecteer Race</Label>
                    <Select 
                      value={selectedRace} 
                      onValueChange={(val) => {
                        setSelectedRace(val);
                        loadPredictionsForRace(val);
                      }}
                      disabled={raceDeadlineInfo?.deadline_passed}
                    >
                      <SelectTrigger className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {races && races.length > 0 && races.map((race) => {
                          const flagUrl = getRaceFlagIcon(race);
                          return (
                            <SelectItem key={race.race_id} value={race.race_id}>
                              <span className="flex items-center">
                                {flagUrl ? (
                                  <img src={flagUrl} alt="" className="inline-block w-5 h-4 mr-2 rounded-sm object-cover" />
                                ) : (
                                  <span className="mr-2">🏁</span>
                                )}
                                {race.name} - {new Date(race.date).toLocaleDateString('nl-NL')}
                              </span>
                            </SelectItem>
                          );
                        })}
                        {(!races || races.length === 0) && (
                          <SelectItem value="loading" disabled>Laden...</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {selectedRaceData && (
                      <div className="bg-blue-50 dark:bg-[#1a2332] border border-blue-200 dark:border-blue-500/50 rounded-lg p-3">
                        <p className="text-sm text-blue-900 dark:text-white flex items-center">
                          {getRaceFlagIcon(selectedRaceData) ? (
                            <img src={getRaceFlagIcon(selectedRaceData)} alt="" className="inline-block w-6 h-5 mr-2 rounded-sm object-cover" />
                          ) : (
                            <span className="mr-2 text-lg">🏁</span>
                          )}
                          <strong>Circuit:</strong>&nbsp;{selectedRaceData.circuit_name} | {selectedRaceData.country}
                        </p>
                        <p className="text-sm text-red-600 dark:text-red-400 font-semibold mt-1">
                          <strong>Deadline:</strong> {!selectedRaceData.fp1?.date ? 'Nog niet bekend' : new Date(selectedRaceData.fp1.date + 'T' + (selectedRaceData.fp1.time || '00:00') + 'Z').toLocaleString('nl-NL', {
                            timeZoneName: 'short',
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })} (start FP1)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Kwalificatie Section */}
                  <div className="bg-blue-50 dark:bg-[#1a2332] border-2 border-blue-200 dark:border-blue-500/50 rounded-lg p-4">
                    <h3 className="text-lg font-bold text-blue-900 dark:text-white mb-4 flex items-center">
                      <Target className="mr-2 text-[#FDB913]" size={20} />
                      Kwalificatie Voorspellingen
                    </h3>
                    
                    <div className="space-y-3 mb-4">
                      {/* P1 always visible */}
                      <div>
                        <Label className="mb-2 block text-blue-800 dark:text-white">Pole Position (P1)</Label>
                        <Select value={qualifyingWinner} onValueChange={setQualifyingWinner} disabled={raceDeadlineInfo?.deadline_passed}>
                          <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                            <SelectValue placeholder="Selecteer coureur" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers && drivers.length > 0 && drivers.map((driver) => (
                              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                {formatDriverNameLastFirst(driver)} ({driver.team})
                              </SelectItem>
                            ))}
                            {(!drivers || drivers.length === 0) && (
                              <SelectItem value="loading" disabled>Laden...</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* P2 and P3 only if toggle enabled */}
                      {seasonSettings.qualifying_p2p3_enabled && (
                        <>
                          <div>
                            <Label className="mb-2 block">P2</Label>
                            <Select value={qualifyingP2} onValueChange={setQualifyingP2} disabled={raceDeadlineInfo?.deadline_passed}>
                              <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                                <SelectValue placeholder="Selecteer coureur" />
                              </SelectTrigger>
                              <SelectContent>
                                {drivers && drivers.length > 0 && drivers.map((driver) => (
                                  <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                    {formatDriverNameLastFirst(driver)} ({driver.team})
                                  </SelectItem>
                                ))}
                                {(!drivers || drivers.length === 0) && (
                                  <SelectItem value="loading" disabled>Laden...</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="mb-2 block">P3</Label>
                            <Select value={qualifyingP3} onValueChange={setQualifyingP3} disabled={raceDeadlineInfo?.deadline_passed}>
                              <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                                <SelectValue placeholder="Selecteer coureur" />
                              </SelectTrigger>
                              <SelectContent>
                                {drivers && drivers.length > 0 && drivers.map((driver) => (
                                  <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                    {formatDriverNameLastFirst(driver)} ({driver.team})
                                  </SelectItem>
                                ))}
                                {(!drivers || drivers.length === 0) && (
                                  <SelectItem value="loading" disabled>Laden...</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </>
                      )}
                    </div>

                    {seasonSettings.pole_time_enabled && (
                    <div>
                      <Label className="font-semibold mb-2 block">Pole Tijd (Voorspelling)</Label>
                      <Input
                        type="text"
                        placeholder="MM:SS.mmm (bijv. 01:30.456)"
                        value={poleTime}
                        onChange={(e) => setPoleTime(e.target.value)}
                        className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]"
                        disabled={raceDeadlineInfo?.deadline_passed}
                      />
                      <p className="text-sm text-muted-foreground mt-1">
                        Formaat: MM:SS.mmm (bijvoorbeeld 01:30.456)
                      </p>
                    </div>
                    )}
                  </div>

                  {/* Race Section */}
                  <div className="bg-amber-50 dark:bg-[#1a2332] border-2 border-[#FDB913] rounded-lg p-4">
                    <h3 className="text-lg font-bold text-[#FDB913] mb-4 flex items-center">
                      <Flag className="mr-2 text-[#FDB913]" size={20} />
                      Race Voorspellingen
                    </h3>

                    {/* Top 5 */}
                    <div className="mb-4">
                      <Label className="text-base font-semibold mb-3 block text-amber-900 dark:text-white">Top 5 Voorspelling</Label>
                      <div className="space-y-3">
                        {[
                          { label: 'P1 (Winnaar)', value: top5P1, setter: setTop5P1 },
                          { label: 'P2', value: top5P2, setter: setTop5P2 },
                          { label: 'P3', value: top5P3, setter: setTop5P3 },
                          { label: 'P4', value: top5P4, setter: setTop5P4 },
                          { label: 'P5', value: top5P5, setter: setTop5P5 },
                        ].map((pos) => (
                          <div key={pos.label} className="flex items-center space-x-3">
                            <span className="font-bold text-[#FDB913] w-24">{pos.label}</span>
                            <Select value={pos.value} onValueChange={pos.setter}>
                              <SelectTrigger className="flex-1 border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                                <SelectValue placeholder="Selecteer coureur" />
                              </SelectTrigger>
                              <SelectContent>
                                {drivers && drivers.length > 0 && drivers.map((driver) => (
                                  <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                    {formatDriverNameLastFirst(driver)} ({driver.team})
                                  </SelectItem>
                                ))}
                                {(!drivers || drivers.length === 0) && (
                                  <SelectItem value="loading" disabled>Laden...</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Fastest Lap */}
                    {seasonSettings.fastest_lap_enabled && (
                    <div className="mb-4">
                      <Label className="font-semibold mb-2 block text-amber-900 dark:text-white">Snelste Ronde</Label>
                      <Select value={fastestLap} onValueChange={setFastestLap}>
                        <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                          <SelectValue placeholder="Selecteer coureur" />
                        </SelectTrigger>
                        <SelectContent>
                          {drivers && drivers.length > 0 && drivers.map((driver) => (
                            <SelectItem key={driver.driver_id} value={driver.driver_id}>
                              {formatDriverNameLastFirst(driver)} ({driver.team})
                            </SelectItem>
                          ))}
                          {(!drivers || drivers.length === 0) && (
                            <SelectItem value="loading" disabled>Laden...</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    )}

                    {/* Safety Car / VSC / DNF / Rain */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {seasonSettings.safety_car_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block">Safety Car</Label>
                        <Select value={safetyCar} onValueChange={setSafetyCar}>
                          <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                            <SelectValue placeholder="Ja/Nee" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ja">Ja</SelectItem>
                            <SelectItem value="nee">Nee</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      {seasonSettings.virtual_safety_car_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block">Virtual Safety Car</Label>
                        <Select value={virtualSafetyCar} onValueChange={setVirtualSafetyCar}>
                          <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                            <SelectValue placeholder="Ja/Nee" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ja">Ja</SelectItem>
                            <SelectItem value="nee">Nee</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      {seasonSettings.dnf_count_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block">Aantal DNF</Label>
                        <Select value={dnfCount} onValueChange={setDnfCount}>
                          <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                            <SelectValue placeholder="Selecteer aantal" />
                          </SelectTrigger>
                          <SelectContent>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                              <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      {seasonSettings.rain_race_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block">Regen Race</Label>
                        <Select value={rainRace} onValueChange={setRainRace}>
                          <SelectTrigger className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]">
                            <SelectValue placeholder="Ja/Nee" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ja">Ja</SelectItem>
                            <SelectItem value="nee">Nee</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      )}
                    </div>
                  </div>

                  {/* Sprint Weekend Section */}
                  {races.find(r => r.race_id === selectedRace)?.is_sprint_weekend && 
                   (seasonSettings.sprint_race_enabled || seasonSettings.sprint_qualifying_enabled || seasonSettings.sprint_pole_winner_enabled) && (
                  <div className="space-y-4 p-4 bg-purple-50 dark:bg-purple-900/30 rounded-lg border-2 border-purple-300 dark:border-purple-600">
                    <h3 className="font-bold text-purple-800 dark:text-purple-300 flex items-center">
                      <Flag className="mr-2 text-purple-600 dark:text-purple-400" size={20} />
                      🏎️ Sprint Weekend Voorspellingen
                    </h3>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-4">
                      Dit is een sprint weekend! Voorspel ook de sprint race en sprint kwalificatie.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Sprint Race Winner */}
                      {seasonSettings.sprint_race_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block text-purple-800 dark:text-purple-200">
                          Sprint Race Winnaar (10 punten)
                        </Label>
                        <Select value={sprintWinner} onValueChange={setSprintWinner}>
                          <SelectTrigger className="border-purple-600 dark:border-purple-500 focus:border-purple-500 focus:ring-purple-500">
                            <SelectValue placeholder="Selecteer winnaar" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                {formatDriverNameLastFirst(driver)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      {/* Sprint Pole Winner (Sprint Qualifying Winner) */}
                      {seasonSettings.sprint_pole_winner_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block text-purple-800 dark:text-purple-200">
                          Sprint Kwalificatie Winnaar / Pole (10 punten)
                        </Label>
                        <Select value={sprintPoleWinner} onValueChange={setSprintPoleWinner}>
                          <SelectTrigger className="border-purple-600 dark:border-purple-500 focus:border-purple-500 focus:ring-purple-500">
                            <SelectValue placeholder="Selecteer pole winnaar" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((driver) => (
                              <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                {formatDriverNameLastFirst(driver)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      )}

                      {/* Sprint Qualifying Pole Time */}
                      {seasonSettings.sprint_qualifying_enabled && (
                      <div>
                        <Label className="font-semibold mb-2 block text-purple-800 dark:text-purple-200">
                          Sprint Pole Tijd (exact 40 pts, binnen 0.5 sec 10 pts)
                        </Label>
                        <Input
                          type="text"
                          placeholder="MM:SS.mmm (bijv. 01:28.456)"
                          value={sprintPoleTime}
                          onChange={(e) => setSprintPoleTime(e.target.value)}
                          className="border-purple-600 dark:border-purple-500 focus:border-purple-500 focus:ring-purple-500"
                        />
                        <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                          Formaat: minuten:seconden.milliseconden
                        </p>
                      </div>
                      )}
                    </div>
                  </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold text-lg py-6"
                    disabled={raceDeadlineInfo?.deadline_passed}
                  >
                    <TrendingUp className="mr-2" size={20} />
                    {raceDeadlineInfo?.deadline_passed ? 'Deadline Verstreken' : 'Voorspelling Opslaan'}
                  </Button>

                  {/* Delete Prediction Button - only show if user has a prediction */}
                  {(raceStatus?.has_race_prediction || raceStatus?.has_qualifying_prediction) && !raceDeadlineInfo?.deadline_passed && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full border-red-500 text-red-600 hover:bg-red-50 font-semibold mt-3"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="mr-2" size={18} />
                      Voorspelling Verwijderen
                    </Button>
                  )}

                  {/* Delete Confirmation Dialog */}
                  {showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                      <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <h3 className="text-lg font-bold text-red-600 flex items-center mb-4">
                          <Trash2 className="mr-2" size={24} />
                          Voorspelling Verwijderen?
                        </h3>
                        
                        <p className="text-foreground mb-4">
                          Weet je zeker dat je je voorspelling voor <strong>{races.find(r => r.race_id === selectedRace)?.name}</strong> wilt verwijderen?
                        </p>
                        
                        <p className="text-sm text-muted-foreground mb-4">
                          Dit verwijdert zowel je kwalificatie als je race voorspelling. Je kunt daarna een nieuwe voorspelling indienen.
                        </p>
                        
                        <div className="flex space-x-3">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setShowDeleteConfirm(false)}
                            disabled={deleting}
                          >
                            Annuleren
                          </Button>
                          <Button
                            variant="destructive"
                            className="flex-1"
                            onClick={handleDeletePrediction}
                            disabled={deleting}
                          >
                            {deleting ? 'Verwijderen...' : 'Ja, Verwijderen'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Championship Predictions */}
          <TabsContent value="championship">
            <Card className="border-2 border-[#FDB913]">
              <CardHeader className="bg-gradient-to-r from-amber-100 to-amber-50 dark:from-[#1a2332] dark:to-[#2d3e50]">
                <CardTitle className="text-amber-900 dark:text-[#FDB913]">Kampioenschap Voorspelling</CardTitle>
                <CardDescription className="text-amber-700 dark:text-gray-300">
                  Voorspel de wereldkampioen en constructeurs kampioen van 2026
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Deadline Warning */}
                {championshipDeadlineInfo?.deadline_passed && (
                  <div className="bg-red-500/20 dark:bg-red-900/40 border-2 border-red-500 rounded-lg p-4 mb-6">
                    <p className="text-red-800 dark:text-red-300 font-bold text-center">
                      ⚠️ DEADLINE VERSTREKEN
                    </p>
                    <p className="text-red-700 dark:text-red-400 text-center mt-2">
                      De kampioenschap voorspellingen zijn gesloten. Je kunt geen voorspellingen meer indienen of wijzigen.
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleChampionshipPrediction} className="space-y-6">
                  {/* World Champion - Top 3 */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold mb-2 block flex items-center space-x-2">
                      <Trophy className="text-[#FDB913]" size={20} />
                      <span>Wereldkampioen 2026 - Top 3</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Voorspel de top 3 van het coureurs kampioenschap
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {[0, 1, 2].map((idx) => (
                        <div key={`wdc-${idx}`}>
                          <Label className="text-xs text-muted-foreground mb-1 block">P{idx + 1}</Label>
                          <Select 
                            value={worldChampionTop3[idx]} 
                            onValueChange={(val) => {
                              const newTop3 = [...worldChampionTop3];
                              newTop3[idx] = val;
                              setWorldChampionTop3(newTop3);
                            }} 
                            disabled={championshipDeadlineInfo?.deadline_passed}
                          >
                            <SelectTrigger className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]">
                              <SelectValue placeholder={`#${idx + 1}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {drivers && drivers.length > 0 && drivers.map((driver) => (
                                <SelectItem key={driver.driver_id} value={driver.driver_id}>
                                  {formatDriverNameLastFirst(driver)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      <span className="font-semibold">Punten:</span> Exact juist: 50 | 1 positie verschil: 40 | 2 posities verschil: 30
                    </div>
                  </div>

                  {/* Constructor Champion - Top 3 */}
                  <div className="space-y-4">
                    <Label className="text-base font-semibold mb-2 block flex items-center space-x-2">
                      <Trophy className="text-[#FDB913]" size={20} />
                      <span>Constructeurs Kampioen 2026 - Top 3</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mb-2">
                      Voorspel de top 3 van het constructeurs kampioenschap
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {[0, 1, 2].map((idx) => (
                        <div key={`wcc-${idx}`}>
                          <Label className="text-xs text-muted-foreground mb-1 block">P{idx + 1}</Label>
                          <Select 
                            value={constructorChampionTop3[idx]} 
                            onValueChange={(val) => {
                              const newTop3 = [...constructorChampionTop3];
                              newTop3[idx] = val;
                              setConstructorChampionTop3(newTop3);
                            }} 
                            disabled={championshipDeadlineInfo?.deadline_passed}
                          >
                            <SelectTrigger className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]">
                              <SelectValue placeholder={`#${idx + 1}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {teams && teams.length > 0 && teams.map((team) => (
                                <SelectItem key={team.team_id} value={team.team_id}>
                                  {team.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      <span className="font-semibold">Punten:</span> Exact juist: 50 | 1 positie verschil: 40 | 2 posities verschil: 30
                    </div>
                  </div>

                  <div className="bg-amber-50 dark:bg-[#1a2332] border border-amber-200 dark:border-[#FDB913]/50 rounded-lg p-4">
                    <p className="text-sm text-amber-800 dark:text-[#FDB913]">
                      <strong>Let op:</strong> Je kunt je kampioenschap voorspellingen wijzigen tot aan de start van de eerste officiële race (Australië).
                    </p>
                    {races && races.length > 0 && races[0] && (
                      <p className="text-sm text-red-600 dark:text-red-400 font-semibold mt-2">
                        <strong>Deadline:</strong> {new Date(races[0].fp1.date + 'T' + races[0].fp1.time + 'Z').toLocaleString('nl-NL', {
                          timeZoneName: 'short',
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })} (start FP1 {races[0].name})
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold text-lg py-6"
                    disabled={championshipDeadlineInfo?.deadline_passed}
                  >
                    <Trophy className="mr-2" size={20} />
                    {championshipDeadlineInfo?.deadline_passed ? 'Deadline Verstreken' : 'Voorspelling Opslaan'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Voorspel;
